import { NavigateBefore, NavigateNext } from "@mui/icons-material";
import { Box, Button, TextField } from "@mui/material";
import React, { useState } from "react";

const DateRangePicker = ({
  allowFutureDates = true,
  highlightColor = "green",
  dateRange,
  setDateRange,
  Theme,
  EaseSelect = false,
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [hoverDate, setHoverDate] = useState(null);

  const getCalendarDates = (year, month) => {
    const startDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    let dates = [];

    // Fill in empty slots for the first week
    for (let i = 0; i < startDay; i++) {
      dates.push(null);
    }

    // Fill in actual dates of the month
    for (let i = 1; i <= daysInMonth; i++) {
      dates.push(new Date(year, month, i));
    }

    return dates;
  };


  const isFutureDate = (date) => {
    return true; // Allow all dates to be selectable
  };

  const handleDateClick = (date) => {
    // Create new date without timezone adjustment
    const selectedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (!dateRange.startDate) {
      setDateRange({ startDate: selectedDate, endDate: null });
    } else if (!dateRange.endDate) {
      if (selectedDate >= dateRange.startDate) {
        setDateRange(prev => ({ ...prev, endDate: selectedDate }));
        setShowCalendar(false);
      }
    } else if (selectedDate >= dateRange.startDate) {
      setDateRange(prev => ({ ...prev, endDate: selectedDate }));
      setShowCalendar(false);
    }
  };

  const isDateInRange = (date) => {
    if (!date) return false;
    
    if (dateRange.startDate && dateRange.endDate) {
      return date >= dateRange.startDate && date <= dateRange.endDate;
    }
    
    if (dateRange.startDate && hoverDate) {
      const start = new Date(Math.min(dateRange.startDate, hoverDate));
      const end = new Date(Math.max(dateRange.startDate, hoverDate));
      return date >= start && date <= end;
    }
    
    return date?.toDateString() === dateRange.startDate?.toDateString();
  };

  const renderCalendar = (year, month) => {
    const dates = getCalendarDates(year, month);
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: "0px",
          fontFamily: "Inter, sans-serif"
        }}
      >
        {["S", "M", "T", "W", "T", "F", "S"].map((day, i) => (
          <div
            key={i}
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: "bold",
              textAlign: "center",
              color: "black",
              padding: "4px 0",
            }}
          >
            {day}
          </div>
        ))}
        {dates.map((date, index) => (
          <Button
            key={index}
            sx={{
              fontFamily: "Inter, sans-serif",
              minWidth: "42px",
              height: "26px",
              borderRadius: "0",
              px: 0,
              backgroundColor: date
                ? date.toDateString() === dateRange.startDate?.toDateString()
                  ? "red"
                  : date.toDateString() === dateRange.endDate?.toDateString()
                    ? "red"
                    : isDateInRange(date)
                      ? 'rgba(255, 0, 0, 0.1)'
                      : "white"
                : "transparent",
              color: date
                ? date.toDateString() === dateRange.startDate?.toDateString() || date.toDateString() === dateRange.endDate?.toDateString()
                  ? "#fff"
                  : isDateInRange(date)
                    ? "#000"
                    : "black"
                : "transparent",
              pointerEvents: date ? "auto" : "none",
              cursor: date ? "pointer" : "default",
              textAlign: "center",
              "&:hover": {
                background: date && "red",
                color: "#fff",
              },
              ...(date && date.toDateString() === dateRange.startDate?.toDateString() && {
                borderTopLeftRadius: "8px",
                borderBottomLeftRadius: "8px",
              }),
              ...(date && date.toDateString() === dateRange.endDate?.toDateString() && {
                borderTopRightRadius: "8px",
                borderBottomRightRadius: "8px",
              }),
            }}
            onClick={() => date && handleDateClick(date)}
            onMouseEnter={() => date && setHoverDate(date)}
            onMouseLeave={() => setHoverDate(null)}
          >
            {date ? date.getDate() : ""}
          </Button>
        ))}
      </div>
    );
  };

  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };
  const handleClear = () => {
    setDateRange({ startDate: null, endDate: null });
    setShowCalendar(false);
  };
  return (
    <Box sx={{ position: { xs: 'initial', md: "relative" } }}>
      <Box sx={{ display: "flex", flexDirection: { xs: 'column', md: 'row' }, gap: "10px" }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ position: "relative" }}>
          <input
              type="text"
              value={
                dateRange.startDate
                  ? new Date(dateRange.startDate.getTime() - (dateRange.startDate.getTimezoneOffset() * 60000)).toLocaleDateString()
                  : ""
              }
              placeholder="From Date"
              onFocus={() => setShowCalendar(true)}
              readOnly
              style={{
                padding: "8px",
                paddingRight: "30px",
                width: "150px",
                height: "16px",
                borderRadius: "4px",
                border: "1px solid lightgray",
              }}
            />
            <img 
              src={require("../assets/landing_page/new/dateicon.png")} 
              alt="calendar" 
              style={{ 
                position: "absolute",
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                width: "16px",
                height: "16px",
                cursor: "pointer",
                pointerEvents: "none"
              }} 
            />
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ position: "relative" }}>
          <input
              type="text"
              value={
                dateRange.endDate 
                  ? new Date(dateRange.endDate.getTime() - (dateRange.endDate.getTimezoneOffset() * 60000)).toLocaleDateString()
                  : ""
              }
              placeholder="Till Date"
              onFocus={() => setShowCalendar(true)}
              readOnly
              style={{
                padding: "8px",
                paddingRight: "30px",
                width: "150px",
                height: "16px",
                borderRadius: "4px",
                border: "1px solid lightgray",
              }}
            />
            <img 
              src={require("../assets/landing_page/new/dateicon.png")} 
              alt="calendar" 
              style={{ 
                position: "absolute",
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                width: "16px",
                height: "16px",
                cursor: "pointer",
                pointerEvents: "none"
              }} 
            />
          </div>
        </div>
      </Box>

      {showCalendar && (
        <div
          style={{
            position: "absolute",
            top: "40px",
            left: "0",
            background: "white",
            border: "1px solid lightgray",
            padding: "8px",
            borderRadius: "4px",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              fontFamily: "Inter, sans-serif",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <Button
              onClick={handlePrevMonth}
              sx={{ padding: "5px", minWidth: "min-content" }}
            >
              <NavigateBefore />
            </Button>
            <span>{`${new Date(currentYear, currentMonth).toLocaleString(
              "default",
              { month: "long" }
            )} ${currentYear}`}</span>
            <Button
              onClick={handleNextMonth}
              sx={{ padding: "5px", minWidth: "min-content" }}
            >
              <NavigateNext />
            </Button>
          </div>
          {renderCalendar(currentYear, currentMonth)}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Button
              onClick={() => setShowCalendar(false)}
              style={{ fontFamily: "Inter, sans-serif", marginTop: "8px", padding: "5px", color: "red" }}
            >
              Close
            </Button>
            <Button
              onClick={handleClear}
              style={{ fontFamily: "Inter, sans-serif", marginTop: "8px", padding: "5px", color: "red" }}
            >
              Clear
            </Button>
          </div>
        </div>
      )}
    </Box>
  );
};

export default DateRangePicker;
